import React, { useEffect, useRef, useState, useCallback } from "react"
import {
  Input,
  Select,
  Selection,
  Button,
  Text,
  Heading,
  Box,
  Flex,
  Loader,
  Alert,
} from "@singita/components"
import {
  Dates,
  NumberField,
  Radio,
  Lodges,
  Winnie,
  PhoneNumber,
  Upload,
  RecaptchaV2,
} from "./components"
import { getFormLayoutStyles, getLabel } from "./utils/helpers"
import { Translation } from "react-i18next"
import { useRecaptchaFallback } from "./hooks/useRecaptchaFallback"

const RECAPTCHA_SITE_KEY = process.env.GATSBY_RECAPTCHA_SITE_KEY || ""

const FormBuilder = ({
  reference,
  fields,
  formId,
  onSubmit,
  renderTitle,
  onChange,
  errors,
  formStyles,
  buttonText = "Submit",
  form,
  setForm,
  isLoading,
}) => {
  const recaptchaRef = useRef(null)
  const [recaptchaError, setRecaptchaError] = useState(null)
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)

  // Use the v2 fallback hook
  const {
    isV2Visible,
    v2Token,
    isV2Loading,
    v2Error,
    isV2Ready,
    showV2Challenge,
    hideV2Challenge,
    resetV2,
    v2ContainerRef,
  } = useRecaptchaFallback()

  // Check if reCAPTCHA is fully ready
  const checkRecaptchaReady = useCallback(() => {
    return !!(
      window.grecaptcha &&
      window.grecaptcha.enterprise &&
      typeof window.grecaptcha.enterprise.execute === "function"
    )
  }, [])

  // Polling function to wait for reCAPTCHA to be fully ready
  const waitForRecaptcha = useCallback(() => {
    return new Promise((resolve, reject) => {
      let attempts = 0
      const maxAttempts = 200 // 20 seconds maximum wait

      const checkReady = () => {
        if (checkRecaptchaReady()) {
          setIsRecaptchaReady(true)
          resolve(true)
          return
        }

        attempts++
        if (attempts >= maxAttempts) {
          reject(new Error("reCAPTCHA failed to initialize within timeout"))
          return
        }

        setTimeout(checkReady, 100)
      }

      checkReady()
    })
  }, [checkRecaptchaReady])

  const initializeRecaptcha = useCallback(async () => {
    // Prevent multiple initialization attempts
    if (isInitializing || isRecaptchaReady) return

    // Check if already loaded
    if (checkRecaptchaReady()) {
      setIsRecaptchaReady(true)
      return
    }

    // Check if script already exists
    if (document.querySelector("#recaptcha-script")) {
      try {
        await waitForRecaptcha()
      } catch (error) {
        console.error("reCAPTCHA initialization timeout:", error)
        setRecaptchaError(
          "Failed to initialize reCAPTCHA. Please refresh and try again."
        )
      }
      return
    }

    if (!RECAPTCHA_SITE_KEY) {
      console.error("Missing RECAPTCHA_SITE_KEY")
      setRecaptchaError("reCAPTCHA configuration error.")
      return
    }

    setIsInitializing(true)
    setRecaptchaError(null)

    const scriptElement = document.createElement("script")
    scriptElement.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`
    scriptElement.async = true
    scriptElement.id = "recaptcha-script"

    scriptElement.onload = async () => {
      try {
        await waitForRecaptcha()
      } catch (error) {
        setRecaptchaError(
          "Failed to initialize reCAPTCHA. Please refresh and try again."
        )
      } finally {
        setIsInitializing(false)
      }
    }

    scriptElement.onerror = () => {
      setRecaptchaError(
        "Failed to load reCAPTCHA. Please check your connection and try again."
      )
      setIsInitializing(false)
    }

    document.body.appendChild(scriptElement)
  }, [isInitializing, isRecaptchaReady, checkRecaptchaReady, waitForRecaptcha])

  useEffect(() => {
    // Initialize reCAPTCHA when email field exists (not on every keystroke)
    const hasEmail = !!form.email
    if (hasEmail && !isRecaptchaReady && !isInitializing) {
      initializeRecaptcha()
    }
    // Only re-run when email existence changes (not value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!form.email, isRecaptchaReady, isInitializing])

  const executeRecaptcha = async () => {
    try {
      if (!checkRecaptchaReady()) {
        throw new Error("reCAPTCHA is not ready")
      }

      const action = `submit_${formId.replace(/[^A-Za-z/_]/g, "")}`

      const token = await window.grecaptcha.enterprise.execute(
        RECAPTCHA_SITE_KEY,
        {
          action: action,
        }
      )

      if (!token) {
        throw new Error("No token received from reCAPTCHA")
      }

      return token
    } catch (error) {
      console.error("reCAPTCHA execution error:", error)
      throw new Error("Failed to verify. Please try again.")
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setRecaptchaError(null)
    setAttemptedSubmit(true)

    // Check if reCAPTCHA is ready
    if (!isRecaptchaReady || !checkRecaptchaReady()) {
      setRecaptchaError("Please wait for reCAPTCHA to load and try again.")
      return
    }

    try {
      const token = await executeRecaptcha()
      const result = await onSubmit({
        recaptchaToken: token,
        recaptchaAction: `submit_${formId.replace(/[^A-Za-z/_]/g, "")}`,
        recaptchaV2Token: v2Token,
      })

      // If challenge is required, show v2
      if (result && result.requiresChallenge && !v2Token) {
        await showV2Challenge()
      } else if (result && result.recaptchaSuccess) {
        // Success - hide v2 if it was shown
        if (isV2Visible) {
          hideV2Challenge()
        }
        setAttemptedSubmit(false)
      }
    } catch (error) {
      setRecaptchaError(error.message)
      console.error("Form submission error:", error)
    }
  }

  // Auto-resubmit when v2 token is obtained
  useEffect(() => {
    if (v2Token && attemptedSubmit && isV2Visible) {
      // Trigger form submission again with v2 token
      handleSubmit({ preventDefault: () => {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v2Token, attemptedSubmit, isV2Visible])

  // Determine if form should be disabled
  // Don't disable while reCAPTCHA is initializing to prevent focus loss
  const isFormDisabled = isLoading || isV2Loading

  return (
    <Box>
      {renderTitle ? renderTitle() : null}
      <Flex as="form" onSubmit={handleSubmit} ref={reference} {...formStyles}>
        {fields.map(
          ({
            name,
            type,
            label,
            options,
            styles,
            placeholder = "",
            variant = "standard",
            size,
            disabled,
            min,
            max,
            orientation,
            group = [],
            initialValue,
            bg,
            required,
            description,
          }) => {
            const layoutStyles = getFormLayoutStyles(styles, variant)
            const realLabel = getLabel(label, required)

            switch (type) {
              case "multiSelect":
                const value = form[name]
                  ? form[name].reduce((result, item) => {
                      const getOption = options.find(o => o.value === item)
                      if (getOption) {
                        result.push({ ...getOption, key: getOption.value })
                      }
                      return result
                    }, [])
                  : []

                return (
                  <Box {...layoutStyles}>
                    <Select
                      id={name}
                      label={realLabel}
                      name={name}
                      options={options.map(option => {
                        return {
                          ...option,
                          key: option.value,
                        }
                      })}
                      onChange={option => {
                        if (option && option.length) {
                          const optionArray = option.map(o => o.value)
                          setForm({ ...form, [name]: optionArray })
                        } else {
                          setForm({ ...form, [name]: [] })
                        }
                      }}
                      placeholder={placeholder}
                      bg="white"
                      noOptionsMessage="No further options"
                      error={errors[name]}
                      isMulti={true}
                      value={value}
                      disabled={isFormDisabled}
                    />
                  </Box>
                )
              case "select":
                return (
                  <Box {...layoutStyles}>
                    <Select
                      label={realLabel}
                      placeholder={placeholder}
                      options={options.map(option => {
                        return {
                          ...option,
                          key: option.value,
                        }
                      })}
                      onChange={option => {
                        setForm({ ...form, [name]: option.value })
                      }}
                      name={name}
                      error={errors[name]}
                      bg="white"
                      value={options.find(
                        option => option.value === form[name]
                      )}
                      disabled={isFormDisabled}
                    />
                    {description && <Text size="tiny">{description}</Text>}
                  </Box>
                )
              case "phone":
                return (
                  <Box {...layoutStyles}>
                    <PhoneNumber
                      form={form}
                      setForm={setForm}
                      name={name}
                      label={realLabel}
                      error={errors[name]}
                      bg={bg}
                      disabled={isFormDisabled}
                    />
                  </Box>
                )
              case "input":
                return (
                  <Box {...layoutStyles}>
                    <Input
                      variant={variant}
                      size="standard"
                      name={name}
                      label={realLabel}
                      type="input"
                      bg="white"
                      onChange={onChange}
                      error={errors[name]}
                      value={form[name] || ""}
                      autoComplete="on"
                      disabled={isFormDisabled || disabled}
                      placeholder={placeholder}
                    />
                  </Box>
                )
              case "email":
                return (
                  <Box {...layoutStyles}>
                    <Input
                      variant={variant}
                      size="standard"
                      name={name}
                      label={realLabel}
                      type="input"
                      bg="white"
                      onChange={onChange}
                      error={errors[name]}
                      value={form[name] || ""}
                      autoComplete="on"
                      disabled={isFormDisabled}
                      placeholder={placeholder}
                    />
                  </Box>
                )
              case "textarea":
                return (
                  <Box {...layoutStyles}>
                    <Input
                      variant={variant}
                      size="standard"
                      name={name}
                      label={realLabel}
                      type="textarea"
                      bg="white"
                      onChange={onChange}
                      error={errors[name]}
                      value={form[name] || ""}
                      disabled={isFormDisabled}
                      placeholder={placeholder}
                    />
                  </Box>
                )
              case "checkbox":
                return (
                  <Box {...layoutStyles}>
                    <Selection
                      label={realLabel}
                      orientation="left"
                      isSpaced={false}
                      onChange={onChange}
                      name={name}
                      error={errors[name]}
                      checked={form[name] || false}
                      value={form[name] || ""}
                      disabled={isFormDisabled}
                      placeholder={placeholder}
                    />
                  </Box>
                )
              case "radio":
                return (
                  <Box {...layoutStyles}>
                    <Radio
                      label={realLabel}
                      group={group}
                      onChange={onChange}
                      name={name}
                      error={errors[name]}
                      form={form}
                      disabled={isFormDisabled}
                    />
                  </Box>
                )
              case "text":
                return (
                  <Box {...layoutStyles}>
                    <Text size={size}>{label}</Text>
                  </Box>
                )
              case "heading":
                return (
                  <Box {...layoutStyles}>
                    <Heading size={size}>{label}</Heading>
                  </Box>
                )
              case "upload":
                return (
                  <Box {...layoutStyles}>
                    <Upload
                      name={name}
                      placeholder={placeholder}
                      setForm={setForm}
                      form={form}
                      label={realLabel}
                      error={errors[name]}
                      disabled={isFormDisabled}
                    />
                  </Box>
                )
              case "number":
                return (
                  <Box {...layoutStyles}>
                    <NumberField
                      name={name}
                      label={realLabel}
                      disabled={disabled || isFormDisabled}
                      initialValue={initialValue}
                      min={min}
                      max={max}
                      orientation={orientation}
                      form={form}
                      setForm={setForm}
                    />
                  </Box>
                )
              case "date":
                return (
                  <Box {...layoutStyles}>
                    <Dates
                      form={form}
                      setForm={setForm}
                      label={label}
                      name={name}
                      disabled={isFormDisabled}
                    />
                  </Box>
                )
              case "lodges":
                return (
                  <Box {...layoutStyles}>
                    <Lodges
                      label={realLabel}
                      placeholder={placeholder}
                      name={name}
                      error={errors[name]}
                      form={form}
                      setForm={setForm}
                      disabled={isFormDisabled}
                    />
                  </Box>
                )
              default:
                return null
            }
          }
        )}
        <Winnie onChange={onChange} />

        {/* Invisible reCAPTCHA element */}
        <div id="recaptcha" ref={recaptchaRef}></div>

        {/* Show button here when v2 is NOT visible */}
        {!isV2Visible && (
          <>
            {recaptchaError && (
              <Alert variant="standard">
                <Text>{recaptchaError}</Text>
              </Alert>
            )}

            <Flex justifyContent="flex-start">
              {isLoading ? (
                <Loader isActive={isLoading} size={100} />
              ) : (
                <Button
                  type="submit"
                  disabled={isFormDisabled || (form.email && !isRecaptchaReady)}
                >
                  {isInitializing ? "Loading..." : buttonText}
                </Button>
              )}
            </Flex>
          </>
        )}

        {/* Visible reCAPTCHA v2 Challenge with button below */}
        {isV2Visible && (
          <Box width="100%">
            <RecaptchaV2
              containerRef={v2ContainerRef}
              isVisible={isV2Visible}
              isLoading={isV2Loading}
              error={v2Error}
              hasToken={!!v2Token}
            />

            <Box mt={3}>
              {isLoading ? (
                <Flex justifyContent="flex-start" alignItems="center" py={3}>
                  <Loader isActive={isLoading} size={100} />
                </Flex>
              ) : (
                <Button
                  type="submit"
                  disabled={!v2Token}
                  style={{
                    opacity: !v2Token ? 0.5 : 1,
                    cursor: !v2Token ? "not-allowed" : "pointer",
                  }}
                >
                  {v2Token ? buttonText : "Complete Verification"}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Flex>
      {Object.keys(errors).length ? (
        <Flex>
          <Box mt={[1]}>
            <Text color="statusError" size="small">
              <Translation>{t => t("form.required-fields")}</Translation>
            </Text>
          </Box>
        </Flex>
      ) : null}
      <Box mt={4} textAlign="left">
        <Text size="tiny" color="gray.500">
          This form is protected by reCAPTCHA Enterprise and the Google{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Terms of Service
          </a>{" "}
          apply.
        </Text>
      </Box>
    </Box>
  )
}

export default FormBuilder
